import { FundriseRouteConfig } from 'vue-router';

/* eslint-disable @typescript-eslint/explicit-function-return-type */

function getStepNumber(routeName: string, isReordered: boolean): number {
	const controlSteps: Record<string, number> = {
		'redemption-request-start-REIT': 1,
		'redemption-request-interstitial-REIT': 2,
		'redemption-request-form-REIT': 3,
		'redemption-request-review-REIT': 4,
		'redemption-request-survey-REIT': 5,
		'redemption-request-agreements-REIT': 6,
		'redemption-request-success-REIT': 7
	};

	const reorderedSteps: Record<string, number> = {
		'redemption-request-start-REIT': 1,
		'redemption-request-survey-REIT': 2,
		'redemption-request-interstitial-REIT': 3,
		'redemption-request-form-REIT': 4,
		'redemption-request-review-REIT': 5,
		'redemption-request-agreements-REIT': 6,
		'redemption-request-success-REIT': 7
	};

	return isReordered ? reorderedSteps[routeName] : controlSteps[routeName];
}

const redemptionRequestRoutes: Array<FundriseRouteConfig> = [
	{
		path: '/reits/redemptions/request/',
		name: 'redemption-request-REIT',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import('@views/redemption-request/redemption-request.vue'),
		beforeEnter: async (to, from, next) => {
			const { useRedemptionRequestStore } = await import('@stores/redemption-request');
			const redemptionRequestStore = useRedemptionRequestStore();
			const { useInvestmentEntityStore } = await import('@stores/investment-entity');
			const investmentEntityStore = useInvestmentEntityStore();

			await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();
			await redemptionRequestStore.fetchRedemptionFlowReorderVariant();
			redemptionRequestStore.redemptionType = 'REIT';
			await redemptionRequestStore.fetchAndStoreMetadata();
			await investmentEntityStore.storeBankAccounts();
			next();
		},
		redirect: { name: 'redemption-request-start-REIT' },
		children: [
			{
				path: 'start',
				name: 'redemption-request-start-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-start-REIT',
						redemptionRequestStore.isReorderedFlow
					);
					next();
				},
				component: () => import('@views/redemption-request/redemption-request-start.vue')
			},
			{
				path: 'form',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				redirect: { name: 'redemption-request-start-REIT' }
			},
			{
				path: 'message',
				name: 'redemption-request-interstitial-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS', suppressDefaultMxpViewEvent: true },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-interstitial-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();

					if (redemptionRequestStore.showRedemptionRequestInterstitial) {
						// In reordered flow, survey must be completed before continuing interstitial
						if (redemptionRequestStore.isReorderedFlow && !redemptionRequestStore.completedSurvey) {
							next({ name: 'redemption-request-survey-REIT', replace: true });
						} else {
							next();
						}
					} else {
						next({ name: 'redemption-request-REIT' });
					}
				},
				component: () => import('@views/redemption-request/redemption-request-interstitial.vue')
			},
			{
				path: 'request-form',
				name: 'redemption-request-form-REIT',
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-form-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					if (redemptionRequestStore.redemptionPreference === 'FULL') {
						next({ name: 'redemption-request-review-REIT', replace: true });
					} else if (!redemptionRequestStore.hasRedeemableReits) {
						next({ name: 'redemption-request-start-REIT', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else if (redemptionRequestStore.isReorderedFlow) {
						// In reordered flow, survey and interstitial must be completed before form
						if (!redemptionRequestStore.completedSurvey) {
							next({ name: 'redemption-request-survey-REIT', replace: true });
						} else {
							next();
						}
					} else {
						next();
					}
				},
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-form-reit.vue')
			},
			{
				path: 'review',
				name: 'redemption-request-review-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-review-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					if (!redemptionRequestStore.hasRedeemableReits || !redemptionRequestStore.hasRequests) {
						next({ name: 'redemption-request-start-REIT', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else {
						redemptionRequestStore.storeRedemptionPreview();
						redemptionRequestStore.showProceeds = false;
						next();
					}
				},
				component: () => import('@views/redemption-request/redemption-request-review.vue')
			},
			{
				path: 'survey',
				name: 'redemption-request-survey-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-survey-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					// In reordered flow, survey comes after start
					// In control flow, survey comes after review
					if (redemptionRequestStore.isReorderedFlow) {
						// Survey can be accessed directly after start in reordered flow
						next();
					} else {
						// Control flow: survey requires review to be completed
						if (redemptionRequestStore.redemptionPreview === null) {
							next({ name: 'redemption-request-review-REIT', replace: true });
						} else if (redemptionRequestStore.submitted) {
							next({ name: 'account-overview', replace: true });
						} else {
							next();
						}
					}
				},
				component: () => import('@views/redemption-request/redemption-request-survey.vue')
			},
			{
				path: 'acknowledgements',
				name: 'redemption-request-agreements-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-agreements-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					// In reordered flow, survey is already completed earlier
					// In control flow, survey must be completed before agreements
					if (!redemptionRequestStore.isReorderedFlow && !redemptionRequestStore.completedSurvey) {
						next({ name: 'redemption-request-survey-REIT', replace: true });
					} else if (!redemptionRequestStore.hasRequests) {
						next({ name: 'redemption-request-start-REIT', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else {
						next();
					}
				},
				component: () => import('@views/redemption-request/redemption-request-agreements.vue')
			},
			{
				path: 'success',
				name: 'redemption-request-success-REIT',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					to.meta.step = getStepNumber(
						'redemption-request-success-REIT',
						redemptionRequestStore.isReorderedFlow
					);

					if (
						redemptionRequestStore.redemptionPreview === null ||
						redemptionRequestStore.redemptionRequestGroupId === ''
					) {
						next({ name: 'redemption-request-start-REIT', replace: true });
					} else {
						next();
					}
				},
				component: () => import('@views/redemption-request/redemption-request-success.vue')
			}
		]
	},
	{
		path: '/ipo/redemptions/request/',
		name: 'redemption-request-IPO',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import('@views/redemption-request/redemption-request.vue'),
		beforeEnter: async (to, from, next) => {
			const { useRedemptionRequestStore } = await import('@stores/redemption-request');
			const redemptionRequestStore = useRedemptionRequestStore();
			const { useInvestmentEntityStore } = await import('@stores/investment-entity');
			const investmentEntityStore = useInvestmentEntityStore();

			redemptionRequestStore.redemptionType = 'IPO';
			await redemptionRequestStore.fetchAndStoreMetadata();
			await investmentEntityStore.storeBankAccounts();
			next();
		},
		redirect: { name: 'redemption-request-start-IPO' },
		children: [
			{
				path: 'start',
				name: 'redemption-request-start-IPO',
				meta: { step: 1, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-start.vue')
			},
			{
				path: 'form',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				redirect: { name: 'redemption-request-start-IPO' }
			},
			{
				path: 'message',
				name: 'redemption-request-interstitial-IPO',
				meta: { step: 2, minTokenScopeRequired: 'FULL_ACCESS', suppressDefaultMxpViewEvent: true },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();

					if (redemptionRequestStore.showRedemptionRequestInterstitial) {
						next();
					} else {
						next({ name: 'redemption-request-IPO' });
					}
				},
				component: () => import('@views/redemption-request/redemption-request-interstitial.vue')
			},
			{
				path: 'redemption-preference',
				name: 'redemption-request-preference-IPO',
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					if (!redemptionRequestStore.hasRedeemableReits) {
						next({ name: 'redemption-request-start-IPO', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else {
						next();
					}
				},
				meta: { step: 3, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-preference.vue')
			},
			{
				path: 'request-form',
				name: 'redemption-request-form-IPO',
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					if (redemptionRequestStore.redemptionPreference === 'FULL') {
						next({ name: 'redemption-request-agreements-IPO', replace: true });
					} else if (!redemptionRequestStore.hasRedeemableReits) {
						next({ name: 'redemption-request-start-IPO', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else {
						next();
					}
				},
				meta: { step: 4, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-form-ipo.vue')
			},
			{
				path: 'acknowledgements',
				name: 'redemption-request-agreements-IPO',
				meta: { step: 5, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					if (!redemptionRequestStore.hasRequests) {
						next({ name: 'redemption-request-start-IPO', replace: true });
					} else if (redemptionRequestStore.submitted) {
						next({ name: 'account-overview', replace: true });
					} else {
						await redemptionRequestStore.storeRedemptionPreview();
						redemptionRequestStore.showProceeds = false;
						next();
					}
				},
				component: () => import('@views/redemption-request/redemption-request-agreements.vue')
			},
			{
				path: 'success',
				name: 'redemption-request-success-IPO',
				meta: { step: 6, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const { useRedemptionRequestStore } = await import('@stores/redemption-request');
					const redemptionRequestStore = useRedemptionRequestStore();

					if (
						redemptionRequestStore.redemptionPreview === null ||
						redemptionRequestStore.redemptionRequestGroupId === ''
					) {
						next({ name: 'redemption-request-start-IPO', replace: true });
					} else {
						next();
					}
				},
				component: () => import('@views/redemption-request/redemption-request-success.vue')
			}
		]
	}
];
export default redemptionRequestRoutes;
